import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, 
  Wifi,
  Smartphone,
  Zap,
  Droplets,
  Receipt,
  Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

// Provider icons mapping
const providerIcons = {
  "flow_ja": { icon: Wifi, color: "bg-blue-500", gradient: "from-blue-500 to-blue-700" },
  "digicel_ja": { icon: Smartphone, color: "bg-red-500", gradient: "from-red-500 to-red-700" },
  "jps_ja": { icon: Zap, color: "bg-yellow-500", gradient: "from-yellow-500 to-yellow-700" },
  "nwc_ja": { icon: Droplets, color: "bg-cyan-500", gradient: "from-cyan-500 to-cyan-700" },
};

const AddBiller = ({ user }) => {
  const navigate = useNavigate();
  const { providerId } = useParams();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    accountNumber: "",
    nickname: ""
  });

  // Fetch provider details
  const { data: providers } = useQuery({
    queryKey: ["billProviders"],
    queryFn: async () => {
      const response = await axios.get(`${API}/bills/providers`);
      return response.data;
    }
  });

  const provider = providers?.find(p => p.provider_id === providerId);

  const addBillerMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/bills/add-biller`, {
        provider_id: providerId,
        account_number: formData.accountNumber,
        nickname: formData.nickname || null
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["myBillers"]);
      setStep(3);
      toast.success("Biller added successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to add biller");
    }
  });

  const handleContinue = () => {
    if (step === 1) {
      if (!formData.accountNumber || formData.accountNumber.length < 6) {
        toast.error("Enter a valid account number");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      addBillerMutation.mutate();
    }
  };

  if (!provider) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const iconConfig = providerIcons[providerId] || { 
    icon: Receipt, 
    color: "bg-gray-500",
    gradient: "from-gray-500 to-gray-700"
  };
  const Icon = iconConfig.icon;

  return (
    <div className="min-h-screen bg-background" data-testid="add-biller-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => step > 1 && step < 3 ? setStep(step - 1) : navigate("/bills")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">
          {step === 3 ? "Biller Added" : `Add ${provider.name.split(" ")[0]}`}
        </h1>
      </header>

      <main className="px-5 max-w-lg mx-auto">
        {/* Progress */}
        {step < 3 && (
          <div className="flex gap-2 mb-8">
            {[1, 2].map(i => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? "bg-primary" : "bg-secondary"
                }`}
              />
            ))}
          </div>
        )}

        {/* Provider Card */}
        <div className={`rounded-3xl p-6 mb-6 bg-gradient-to-br ${iconConfig.gradient}`}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
              <Icon className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="font-heading font-semibold text-lg text-white">
                {provider.name}
              </p>
              <p className="text-sm text-white/70">{provider.category}</p>
            </div>
          </div>
        </div>

        {/* Step 1: Account Number */}
        {step === 1 && (
          <div className="animate-slide-up">
            <div className="glass rounded-3xl p-6 mb-6">
              <h2 className="font-heading font-medium text-lg mb-4">
                Enter your {provider.account_label.toLowerCase()}
              </h2>
              <div>
                <Label htmlFor="accountNumber">{provider.account_label}</Label>
                <Input
                  id="accountNumber"
                  data-testid="account-number-input"
                  type="text"
                  placeholder={provider.account_format}
                  className="input-glass mt-2 text-lg font-mono tracking-wider"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Find this on your bill statement
                </p>
              </div>
            </div>
            
            <Button
              className="w-full btn-primary h-14 text-base"
              onClick={handleContinue}
              data-testid="continue-btn"
            >
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: Nickname */}
        {step === 2 && (
          <div className="animate-slide-up">
            <div className="glass rounded-3xl p-6 mb-6">
              <h2 className="font-heading font-medium text-lg mb-4">
                Give it a nickname (optional)
              </h2>
              <div>
                <Label htmlFor="nickname">Nickname</Label>
                <Input
                  id="nickname"
                  data-testid="nickname-input"
                  type="text"
                  placeholder="e.g. Home Light Bill"
                  className="input-glass mt-2"
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Makes it easier to identify this bill later
                </p>
              </div>
              
              {/* Summary */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Provider</span>
                  <span className="text-foreground">{provider.name}</span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-muted-foreground">{provider.account_label}</span>
                  <span className="text-foreground font-mono">{formData.accountNumber}</span>
                </div>
              </div>
            </div>
            
            <Button
              className="w-full btn-primary h-14 text-base"
              onClick={handleContinue}
              disabled={addBillerMutation.isPending}
              data-testid="add-biller-btn"
            >
              {addBillerMutation.isPending ? "Adding..." : "Add Biller"}
            </Button>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="animate-slide-up text-center">
            <div className="glass rounded-3xl p-8 mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                <Check className="w-10 h-10 text-primary" />
              </div>
              <h2 className="font-heading font-bold text-2xl mb-2">Biller Added!</h2>
              <p className="text-muted-foreground mb-6">
                You can now pay your {provider.name} bill anytime
              </p>
              
              <div className="text-left space-y-3 bg-white/5 rounded-2xl p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Provider</span>
                  <span className="text-foreground">{provider.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Account</span>
                  <span className="text-foreground font-mono">
                    {"*".repeat(formData.accountNumber.length - 4) + formData.accountNumber.slice(-4)}
                  </span>
                </div>
                {formData.nickname && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Nickname</span>
                    <span className="text-foreground">{formData.nickname}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                className="w-full btn-primary h-14 text-base"
                onClick={() => navigate("/bills")}
                data-testid="view-bills-btn"
              >
                View My Bills
              </Button>
              <Button
                variant="outline"
                className="w-full btn-secondary h-14 text-base"
                onClick={() => navigate("/dashboard")}
              >
                Back to Home
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AddBiller;
